<template>
  <nav id="app-navigation" aria-label="Navegación principal" class="w-full bg-sky-950 overflow-hidden sticky top-0 nav-shell">
    <div class="nav-shell-inner flex flex-col">
      <button v-if="mobile" type="button" aria-label="Cerrar menú de navegación"
        class="self-end shrink-0 m-2 text-white rounded-lg hover:bg-sky-900" @click="$emit('close')">✕</button>
      <LoadingNavCompany v-if="loading" />
      <div class="flex py-2 h-[3.5rem] px-4 bg-sky-950" v-else>
        <div
          class="col-span-1 bg-sky-950 h-11 w-11 flex items-center justify-center rounded-full overflow-hidden flex-shrink-0">
          <div class="h-11 w-11 flex items-center justify-center flex-shrink-0">
            <img src="/img/profiles/default.png" v-if="!dataCompany.logo" alt="" />
            <img :src="buildApiResourceUrl(dataCompany.logo)" v-else alt=""
              @error="clearBrokenCompanyLogo" />
          </div>
        </div>
        <div class="col-span-11 text-white font-bold ml-2 mt-0 mb-0 small-hidden uppercase flex-wrap md:flex-nowrap">
          <span class="truncate overflow-hidden w-[150px] block">{{
            dataCompany.company
          }}</span>
          <span
            class="flex items-center gap-1 text-[13px] bg-sky-900 w-[150px] truncate overflow-hidden text-orange-400 font-semibold px-2 rounded-lg">
            <span class="w-2 h-2 rounded-full bg-green-400 block animate-pulse"></span>
            {{ dataCompany.store }}
          </span>
        </div>
      </div>
      <div class="flex-1 min-h-0 overflow-y-auto scroll-nav pt-3">
        <ul class="text-blue-100 text-sm" @click="handleNavigation">
          <li>
            <router-link to="/" aria-label="Dashboard"
              class="bg-transparent hover:bg-sky-900 transition-colors flex py-3 px-4 items-center small-center">
              <font-awesome-icon icon="fa-solid fa-gauge" class="w-5 h-5" />
              <span class="ml-2 small-hidden"> Dashboard </span>
            </router-link>
          </li>
          <GetSystemModules />
        </ul>
      </div>
      <button v-if="canInstall" type="button" class="shrink-0 flex items-center gap-2 border-t border-white/10 px-4 py-3 text-sm text-blue-100 hover:bg-sky-900 small-center"
        aria-label="Instalar aplicación" @click="$emit('install')">
        <font-awesome-icon icon="fa-solid fa-download" class="w-5 h-5 shrink-0" />
        <span class="small-hidden">Instalar aplicación</span>
      </button>
    </div>
  </nav>
</template>

<script>
import GetSystemModules from "@/components/SystemModules/GetSystemModules.vue";
import LoadingNavCompany from "@/components/Loadings/LoadingNavCompany.vue";
import { onMounted, ref } from "vue";
import axios from "../../api";
import { errorActions } from "@/alerts";
import { clearStoredSession, getStoredAccessData, hasValidSession } from "@/publicjs/authSession";
import { buildApiResourceUrl } from "@/publicjs/resourceUrl";
export default {
  name: "NavComponent",
  props: { mobile: Boolean, canInstall: Boolean },
  emits: ['close', 'navigate', 'install'],
  components: {
    GetSystemModules,
    LoadingNavCompany,
  },
  setup(props, { emit }) {
    const loading = ref(true);
    const dataCompany = ref({
      company: "",
      store: "",
      logo: null,
    });
    onMounted(async () => {
      loading.value = true;
      const dataUser = getStoredAccessData();

      if (!dataUser || !hasValidSession() || !dataUser?.user?.store) {
        loading.value = false;
        return;
      }

      try {
        const responseStore = await axios
          .get("sucursales/" + dataUser.user.store)
          .catch((error) => {
            errorActions(error);
          });
        if (!responseStore?.data) {
          loading.value = false;
          return;
        }
        if (responseStore.data.status === "Expired token") {
          clearStoredSession();
          window.location.href = "/login";
          return;
        }
        const responseCompany = await axios
          .get("empresa/" + responseStore.data.idempresa)
          .catch((error) => {
            errorActions(error);
          });
        if (!responseCompany?.data) {
          loading.value = false;
          return;
        }
        dataCompany.value.store = await responseStore.data.razon_social;
        dataCompany.value.company = await responseCompany.data.razon_social;
        dataCompany.value.logo = await responseCompany.data.logo;
      } catch (error) {
        errorActions(error);
      }

      loading.value = false;
    });

    const toggleMenu = (e) => {
      const toggle = e.target.closest(".toggle");

      if (!toggle) {
        return;
      }

      const navList = e.currentTarget;
      const subMenu = toggle.nextElementSibling;

      if (!subMenu || !subMenu.classList.contains("sub-menu")) {
        return;
      }

      const shouldOpen = !toggle.classList.contains("active");

      navList.querySelectorAll(".toggle.active").forEach((item) => {
        if (item !== toggle) {
          item.classList.remove("active");
          item.setAttribute('aria-expanded', 'false');
        }
      });

      navList.querySelectorAll(".sub-menu.active").forEach((item) => {
        if (item !== subMenu) {
          item.classList.remove("active");
        }
      });

      toggle.classList.toggle("active", shouldOpen);
      toggle.setAttribute('aria-expanded', String(shouldOpen));
      subMenu.classList.toggle("active", shouldOpen);
    };
    const handleNavigation = event => {
      toggleMenu(event);
      if (event.target.closest('a[href]')) emit('navigate');
    };

    const clearBrokenCompanyLogo = () => {
      dataCompany.value.logo = null;
    };

    return {
      toggleMenu,
      handleNavigation,
      loading,
      dataCompany,
      buildApiResourceUrl,
      clearBrokenCompanyLogo,
    };
  },
};
</script>

<style scoped>
.nav-shell {
  height: var(--app-height, 100dvh);
  min-height: var(--app-height, 100dvh);
  padding-top: var(--safe-top);
  padding-bottom: var(--safe-bottom);
  padding-left: var(--safe-left);
}

.nav-shell-inner {
  height: 100%;
}

.scroll-nav::-webkit-scrollbar {
  width: 5px;
  height: 5px;
  background-color: rgba(0, 0, 0, 0);
  border-radius: 10px;
}

.scroll-nav::-webkit-scrollbar-thumb {
  border-radius: 10px;
  /* box-shadow: inset 0 0 6px var(--purple); */
  background-color: rgba(0, 0, 0, 0.068);
}

.scroll-nav::-webkit-scrollbar-track {
  background-color: transparent;
}

.scroll-nav::-webkit-scrollbar-thumb:hover,
.scroll-nav::-webkit-scrollbar-thumb:active {
  background: #93c5fd;
}

.truncate {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
